import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import { useParams, Link } from 'react-router-dom'
import MasterData from '../../data/MasterData'
import Timetable from './Timetable'
import { stationNumberClass, textLongClass } from '../../utils/ClassHelper'

const INCIDENT_HTML_URL = process.env.INCIDENT_HTML_URL
const DELAY_THRESHOLD = 5

const StationDetail = ({ station, streetViewUrl, hasIncidents, timetables, themeName, fetchStationDetail, changeTimetableCollapsed }) => {
  const intl = useIntl()
  const { stationId } = useParams()
  const locale = intl.locale

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchStationDetail(stationId)
  }, [fetchStationDetail, stationId])

  const getTrainPositionPath = (stationId) => {
    if (MasterData.isOnMainLine(stationId)) {
      return `/lines/main/trains#${stationId}`
    } else if (MasterData.isOnIzuminoLine(stationId)) {
      return `/lines/izumino/trains#${stationId}`
    } else if (MasterData.isOnShinyokohamaLine(stationId)) {
      return `/lines/for_tokyu/trains#${stationId}`
    }
  }

  const renderStopKinds = () => {
    return (
      <ul className="d-StationDetail-stopKind">
        {station.stopKindIds.map((stopKindId, i) => {
          const stopKind = MasterData.trainKindById(stopKindId)
          return (
            <li key={`stopKinds${i}`} className={`d-StationDetail-stopKindLabel ${MasterData.trainKindClassById(stopKind.id)} ${stopKind['is_jr'] ? 'is_jr': ''}`}>{stopKind[`station_detail_name_${locale}`]}</li>
          )
        })}
      </ul>
    )
  }

  const renderDelayInfoElement = () => {
    if (!hasIncidents) return null

    return(
      <div className="d-StationDetail-delayInfo">
        <p className="d-StationDetail-delayInfoText">只今相鉄線では運行情報がございます</p>
        <Link to="/lines/main/trains" className="d-StationDetail-delayInfoButton">列車走行位置を確認する</Link>
        <a href={INCIDENT_HTML_URL} className="d-StationDetail-delayInfoButton">運行情報を確認する</a>
      </div>
    )
  }

  const renderStreetView = () => {
    if (streetViewUrl) {
      return <iframe src={streetViewUrl} className="d-StationDetail-map" />
    }
  }

  const renderThruNote = () => {
    const jrTrainArriving = station.stopKindIds.includes(104)
    const toyokoTrainArriving = station.stopKindIds.includes(204)
    const meguroTrainArriving = station.stopKindIds.includes(214)

    if (!jrTrainArriving && !toyokoTrainArriving && !meguroTrainArriving) {
      return
    }

    return (
      <ul className="d-StationDetail-thruNote">
        { jrTrainArriving && (
          <li className="d-StationDetail-thruNoteItem">
            <span className="d-StationDetail-thruNoteIcon is_jr" />
            <span>
              <FormattedMessage id='StationDetail.thruToJR'>
              </FormattedMessage>
            </span>
          </li>
        )}
        { toyokoTrainArriving && (
          <li className="d-StationDetail-thruNoteItem">
            <span className="d-StationDetail-thruNoteIcon is_toyoko" />
            <span>
              <FormattedMessage id='StationDetail.thruToToyoko'>
              </FormattedMessage>
            </span>
          </li>
        )}
        { meguroTrainArriving && (
          <li className="d-StationDetail-thruNoteItem">
            <span className="d-StationDetail-thruNoteIcon is_meguro" />
            <span>
              <FormattedMessage id='StationDetail.thruToMeguro'>
              </FormattedMessage>
            </span>
          </li>
        )}
      </ul>
    )
  }


  const renderTimeTables = () => {
    if (hasIncidents) return null

    return timetables.map((timetable, index) => {
      return <Timetable
        key={`timetable${index}`}
        {...{timetable, index, changeTimetableCollapsed}}
      />
    })
  }

  const renderTimetableLinkElement = () => {
    if (timetables.length === 1) {
      return (
        <div className="d-StationDetail-buttonRow">
          <a href={timetables[0]['navitime_timetable_url']} className={`d-StationDetail-button is_${themeName}`}>{timetables[0][`toward_${locale}`]}</a>
        </div>
      )
    } else if (timetables.length === 2) {
      return (
        <div className="d-StationDetail-buttonRow">
          <a href={timetables[0]['navitime_timetable_url']} className={`d-StationDetail-button is_${themeName} d-StationDetail-button--2col`}>{timetables[0][`toward_${locale}`]}</a>
          <a href={timetables[1]['navitime_timetable_url']} className={`d-StationDetail-button is_${themeName} d-StationDetail-button--2col`}>{timetables[1][`toward_${locale}`]}</a>
        </div>
      )
    } else if (timetables.length === 3) {
      return (
        <div>
          <div className="d-StationDetail-buttonRow">
            <a href={timetables[0]['navitime_timetable_url']} className={`d-StationDetail-button is_${themeName} d-StationDetail-button--2col`}>{timetables[0][`toward_${locale}`]}</a>
            <a href={timetables[1]['navitime_timetable_url']} className={`d-StationDetail-button is_${themeName} d-StationDetail-button--2col`}>{timetables[1][`toward_${locale}`]}</a>
          </div>
          <div className="d-StationDetail-buttonRow">
            <a href={timetables[2]['navitime_timetable_url']} className={`d-StationDetail-button is_${themeName}`}>{timetables[2][`toward_${locale}`]}</a>
          </div>
        </div>
      )
    }
  }

  const renderStationView = () => {
    return (
      <Link to={`/stations/${station.id}/station_view`} className="d-StationDetail-stationview">
        <p className={`d-StationDetail-stationviewCaption is_${themeName}`}>
          <FormattedMessage id='StationDetail.checkCongestionSituation' values={{br: <br key="br" />}} />
        </p>
        <p className={`d-StationDetail-stationviewTitle is_${themeName}`}>
          Station View
          <span className={`d-StationDetail-stationviewTitleJp is_${themeName}`}>
            <FormattedMessage id='StationDetail.stationView' />
          </span>
        </p>
      </Link>
    )
  }

  const renderStationHeaderName = (nameKey) => {
    // 羽沢駅だけ改行を入れる
    if (station.id === 28 && locale === 'en') {
      return(
        <p className="d-StationDetail-headerNameJp d-StationDetail-headerNameJp-locale-en d-StationDetail-headerNameJp-station-28"><span>Hazawa</span> yokohama-kokudai</p>
      )
    } else if (station.id === 28 && locale === 'ko') {
      return(
        <p className="d-StationDetail-headerNameJp"><span>하자와</span> 요코하마코쿠다이</p>
      )
    } else {
      return(
        <p className={`d-StationDetail-headerNameJp`}>{station[nameKey]}</p>
      )
    }
  }

  if (!station) return null

  // APIから駅情報をとれたとき
  const ekiNumber = station.eki_number
  const nameKey = `name_${locale}`
  const nameEnKey = `name_en`

  return (
    <div>
      <header className={`d-StationDetail-header d-StationDetail-header-${themeName}`}>
        <div className="d-StationDetail-headerInfo">
          <Link to={getTrainPositionPath(station.id)}>
            <div className={`d-StationDetail-headerNumber ${stationNumberClass(ekiNumber, themeName)}`}>
            </div>
            <div className="d-StationDetail-headerName">
              {renderStationHeaderName(nameKey)}
              <p className={`d-StationDetail-headerNameEn d-StationDetail-headerNameEn-${locale}`}>{station[nameEnKey]}</p>
            </div>
          </Link>
        </div>{/* /.d-StationDetail-headerInfo */}
        {renderStopKinds()}
      </header>{/* /.d-StationDetail-header */}

      {renderThruNote()}
      {renderTimeTables()}
      {renderDelayInfoElement()}
      {renderTimetableLinkElement()}
      {renderStationView()}

      <div className="d-StationDetail-content">
        <div className={`d-StationDetail-contentItem is_${themeName}`}>
          <a href={`https://www.sotetsu.co.jp/media/app/train/img/SO${ekiNumber}_layout.png`}>
            <FormattedMessage id='StationDetail.stationMap'>
              {(txt) => <p className={`d-StationDetail-contentTitle is_${themeName}`}>{txt}</p>}
            </FormattedMessage>
            <div className="d-StationDetail-contentImgWrap">
              <div className={`d-StationDetail-contentImg is_map${ekiNumber}`}></div>
            </div>
          </a>
        </div>

        <div className={`d-StationDetail-contentItem is_${themeName}`}>
          <a href={`https://www.sotetsu.co.jp/media/app/train/img/SO${ekiNumber}_position.png`}>
            <FormattedMessage id='StationDetail.stopPositions'>
              {(txt) => <p className={`d-StationDetail-contentTitle is_${themeName}`}>{txt}</p>}
            </FormattedMessage>
            <div className="d-StationDetail-contentImgWrap">
              <div className={`d-StationDetail-contentImg is_position${ekiNumber}`}></div>
            </div>
          </a>
        </div>
      </div>{/* /.d-StationDetail-content */}

      {renderStreetView()}
    </div>
  )
}

StationDetail.propTypes = {
  station: PropTypes.object,
  streetViewUrl: PropTypes.string,
  hasIncidents: PropTypes.bool.isRequired,
  timetables: PropTypes.array.isRequired,
  themeName: PropTypes.string.isRequired,
  fetchStationDetail: PropTypes.func.isRequired,
  changeTimetableCollapsed: PropTypes.func.isRequired,
}

export default StationDetail
